import { SCRIPT_CONFIG } from 'constant/scriptLoader';
import { STONLY_DATA_TRANSMISSION_TARGETS } from 'constant/guides';
import { useEffect } from 'react';
import { useScript } from 'hooks/useScript';
import useUser from 'hooks/useUser';
import { CONFIG } from 'config/environment_variables';
import { useQuarterZip } from 'hooks/integrations/quarterZip';

/** Stonly iframes may post from any `*.stonly.com` HTTPS origin. */
function isTrustedStonlyPostMessageOrigin(origin: string): boolean {
  try {
    const url = new URL(origin);
    if (url.protocol !== 'https:') return false;
    return (
      url.hostname === 'stonly.com' || url.hostname.endsWith('.stonly.com')
    );
  } catch {
    return false;
  }
}

// Utility functions
const createScriptElement = (content: string): HTMLScriptElement => {
  const script = document.createElement('script');
  script.textContent = content;
  return script;
};

const injectScript = (content: string): void => {
  const script = createScriptElement(content);
  document.head.appendChild(script);
};

// Stonly Widget initialization
const initializeStonlyWidget = (): void => {
  const { widgetId } = SCRIPT_CONFIG.STONLY;

  try {
    (window as Window & { STONLY_WID?: string }).STONLY_WID = widgetId;
  } catch {
    // If assignment fails, continue
  }

  const initScript = `
    window.STONLY_WID = '${widgetId}';
    !(function (s, t, o, n, l, y, w, g, d, e) {
      s.StonlyWidget ||
        (((d = s.StonlyWidget =
          function () {
            d._api ? d._api.apply(d, arguments) : d.queue.push(arguments);
          }).scriptPath = n),
        (d.apiPath = l),
        (d.sPath = y),
        (d.queue = []),
        ((g = t.createElement(o)).async = !0),
        (e = new XMLHttpRequest()).open(
          'GET',
          n + 'version?v=' + Date.now(),
          !0,
        ),
        (e.onreadystatechange = function () {
          4 === e.readyState &&
            ((g.src =
              n +
              'stonly-widget.js?v=' +
              (200 === e.status ? e.responseText : Date.now())),
            (w = t.getElementsByTagName(o)[0]).parentNode.insertBefore(g, w));
        }),
        e.send());
    })(window, document, 'script', 'https://stonly.com/js/widget/v2/');
  `;

  injectScript(initScript);
};

// Pylon Widget initialization
const initializePylonWidget = (): void => {
  const initScript = `
    (function () {
      var e = window;
      var t = document;
      var n = function () {
        n.e(arguments);
      };
      n.q = [];
      n.e = function (e) {
        n.q.push(e);
      };
      e.Pylon = n;
      var r = function () {
        var e = t.createElement('script');
        e.setAttribute('type', 'text/javascript');
        e.setAttribute('async', 'true');
        e.setAttribute(
          'src',
          'https://widget.usepylon.com/widget/3ae2d970-2dbb-43f2-b6c6-dab95aaee6df',
        );
        var n = t.getElementsByTagName('script')[0];
        n.parentNode.insertBefore(e, n);
      };
      if (t.readyState === 'complete') {
        r();
      } else if (e.addEventListener) {
        e.addEventListener('load', r, false);
      }
    })();
  `;

  injectScript(initScript);
};

// Quarterzip SDK initialization — sets up the command queue stub before the
// SDK script finishes loading, so callers can invoke `window.Quarterzip.open`
// / `.close` immediately.
const initializeQuarterzipStub = (): void => {
  if (window.Quarterzip) return;

  const queue: unknown[][] = [];
  window.Quarterzip = {
    _q: queue,
    open: (args) => {
      queue.push(['open', args]);
    },
    close: (args) => {
      queue.push(['close', args]);
    },
  };
};

// Custom hook for Stonly Widget
export const useStonlyWidget = () => {
  const { user } = useUser();
  const status = useScript(SCRIPT_CONFIG.STONLY.url, {
    removeOnUnmount: false,
    id: SCRIPT_CONFIG.STONLY.id,
    shouldPreventLoad: !user,
  });

  useEffect(() => {
    if (status === 'ready' && !('StonlyWidget' in window)) {
      initializeStonlyWidget();
    }
  }, [status]);

  return status;
};

// Custom hook for Pylon Widget
export const usePylonWidget = () => {
  const { user } = useUser();
  const status = useScript(SCRIPT_CONFIG.PYLON.url, {
    removeOnUnmount: false,
    id: SCRIPT_CONFIG.PYLON.id,
    shouldPreventLoad: !user,
  });

  useEffect(() => {
    if (status === 'ready' && !('Pylon' in window)) {
      initializePylonWidget();
    }
  }, [status]);

  return status;
};

// Custom hook for Quarterzip SDK. Sets up the command-queue stub, loads the
// SDK, and exposes a `window.openQuarterzipAgent(agentId)` helper that closes
// over the current user — so external surfaces (e.g. Stonly JS actions) can
// open an agent without knowing about the workspace token or user shape.
export const useQuarterzipSdk = () => {
  const { user } = useUser();
  const { openAgent, closeAgent } = useQuarterZip();

  // Initialize the stub synchronously during render — must happen before the
  // script tag is injected so the SDK finds the stub in place when it executes.
  if (typeof window !== 'undefined' && !window.Quarterzip) {
    initializeQuarterzipStub();
  }

  const status = useScript(SCRIPT_CONFIG.QUARTERZIP.url, {
    removeOnUnmount: false,
    id: SCRIPT_CONFIG.QUARTERZIP.id,
  });

  useEffect(() => {
    if (!user) return;

    // Allow embedded Stonly iframes to trigger the Quarterzip agent.
    // Handles two message shapes:
    //   1. Stonly Data Transmission  → { target: 'open-quarterzip-agent', value: '<agentId>' }
    //   2. Legacy custom postMessage → { type: 'OPEN_QUARTERZIP_AGENT', agentId?, user? }
    const handleMessage = (event: MessageEvent) => {
      if (!isTrustedStonlyPostMessageOrigin(event.origin)) return;

      const data = event.data;
      if (!data || typeof data !== 'object') return;

      // ── Path 1: Stonly Data Transmission ───────────────────────────────────
      if (
        data.target === STONLY_DATA_TRANSMISSION_TARGETS.OPEN_QUARTERZIP_AGENT
      ) {
        const messageData = data?.value;
        const agentId =
          typeof messageData === 'string' && messageData.trim() !== ''
            ? messageData.trim()
            : CONFIG.QUARTERZIP_AGENT_ID.trim();

        if (!agentId) return;
        openAgent();
        return;
      }

      // ── Path 2: Legacy custom postMessage (backward compat) ────────────────
      if ((data as { type?: string }).type !== 'OPEN_QUARTERZIP_AGENT') return;

      const agentIdRaw = (data as { agentId?: unknown }).agentId;
      const agentId =
        typeof agentIdRaw === 'string' && agentIdRaw.trim() !== ''
          ? agentIdRaw
          : CONFIG.QUARTERZIP_AGENT_ID.trim();

      if (!agentId) return;

      openAgent();
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [user, openAgent]);

  // Pre-warm: trigger the agent-config HTTP fetch and WebSocket connection as
  // soon as the SDK loads and the user is authenticated. The close() call
  // immediately after hides the widget before it renders, while the browser
  // caches the HTTP response. When the user clicks the help button, the cached
  // response makes the first open() call show the UI without a second click.
  useEffect(() => {
    if (status !== 'ready' || !user) return;
    const agentId = CONFIG.QUARTERZIP_AGENT_ID?.trim();
    if (!agentId || !CONFIG.QUARTERZIP_WORKSPACE_TOKEN) return;

    openAgent();
    closeAgent();
  }, [status, user, openAgent, closeAgent]);

  return status;
};
